import type { V2Area, V2ChecklistItem } from '../domain/inspection';
import type { ReportOutputLanguage } from '../domain/report';
import { splitSentences } from '../domain/wordingGuard';
import { areaDisplayName } from '../i18n/labels';

// THE REPORT WORDING LIBRARY (Owner device test 2026-09-13: "我单项检查后都要给我报告语让我看看对不对 …
// 最后报告里统一的电器-GFCI部分全部报告语统一").
//
// One pure function turns a checklist row's recorded result into the sentence the report prints for
// it; the item sheet shows that same sentence the moment the result is chosen, so what the inspector
// reads on site is what the client reads later. Rows of the same CHECK TYPE (the GFCI receptacles of
// every room, the alarms of every floor …) are also summed up across the house into one paragraph:
// all clear → one uniform sentence; some not → the failing locations first, the clear ones after.
//
// Deterministic and versioned: the wording is data, never the model's. Unable / not-present rows are
// worded by the frozen New York limitation and not-present blocks and get no sentence here.
//
// 2026-09-29 (Owner report review 2026-09-28; ratification 2026-09-13 §8 "a test is written only when
// the record states it"): every check whose sentence names a test or an operation — GFCI, alarms,
// receptacles, windows and doors, bedroom window, garage door, bathroom ventilation — prints the
// tested / operated sentence only when the inspector recorded it (operationalTestPerformed), and
// what was observed otherwise; a deficiency sentence never names a test; the alarm sentences no
// longer claim that both smoke AND carbon monoxide alarms were there.
export const REPORT_WORDING_VERSION = 'report-wording-2026-09-29';

export type CheckTypeId = 'gfci' | 'smokeCo' | 'outlets' | 'windowsDoors' | 'stairsRails' | 'surfaces' | 'egress' | 'tpr' | 'garageDoor' | 'wdi' | 'bathExhaust' | 'kitchenVent';

export const CHECK_TYPE_ORDER: readonly CheckTypeId[] = Object.freeze(['gfci', 'smokeCo', 'outlets', 'windowsDoors', 'stairsRails', 'surfaces', 'egress', 'tpr', 'garageDoor', 'wdi', 'bathExhaust', 'kitchenVent']);

// Checklist template rows that are the same check in different places (src/domain/checklistTemplates.ts).
export const CHECK_TYPE_OF_ROW: Readonly<Record<string, CheckTypeId>> = Object.freeze({
  'ext-gfci': 'gfci',
  'garage-gfci': 'gfci',
  'bsmt-gfci': 'gfci',
  'kit-gfci': 'gfci',
  'bath-gfci': 'gfci',
  'elec-gfci-afci': 'gfci',
  'bsmt-alarm': 'smokeCo',
  'int-alarms': 'smokeCo',
  'bed-alarm': 'smokeCo',
  'safe-alarms-system': 'smokeCo',
  'elec-outlets-sample': 'outlets',
  'int-outlets': 'outlets',
  'bed-outlets': 'outlets',
  'ext-windows-doors': 'windowsDoors',
  'int-windows': 'windowsDoors',
  'int-doors': 'windowsDoors',
  'bed-windows-doors': 'windowsDoors',
  'ext-entry-steps': 'stairsRails',
  'bsmt-stairs': 'stairsRails',
  'int-stairs': 'stairsRails',
  'safe-handrails': 'stairsRails',
  'int-surfaces': 'surfaces',
  'kit-surfaces': 'surfaces',
  'bath-surfaces': 'surfaces',
  'bed-surfaces': 'surfaces',
  'bed-egress': 'egress',
  'safe-egress': 'egress',
  'wh-tpr': 'tpr',
  'safe-tpr': 'tpr',
  'garage-door-reverse': 'garageDoor',
  'safe-garage-door': 'garageDoor',
  'ext-wdi': 'wdi',
  'safe-wdi': 'wdi',
  'bath-exhaust': 'bathExhaust',
  'kit-exhaust-ducting': 'kitchenVent'
});

export function checkTypeOfTemplateRow(templateId: string | null): CheckTypeId | null {
  return templateId !== null && Object.prototype.hasOwnProperty.call(CHECK_TYPE_OF_ROW, templateId) ? CHECK_TYPE_OF_ROW[templateId] : null;
}

interface Bilingual {
  en: string;
  zh: string;
}

interface CheckTypeWording {
  title: Bilingual;
  // {areas} = the locations (already joined in the report language). For a check that has a
  // passTested sentence, `pass` states only what was observed — never a test or an operation.
  pass: Bilingual;
  // For a check whose pass sentence depends on the recorded test / operation fact (TW-3; every such
  // check since 2026-09-29): printed only when the row records operationalTestPerformed === true.
  passTested?: Bilingual;
  // Never names a test or an operation: the row's own sentence and the findings carry what was seen.
  defect: Bilingual;
  unable: Bilingual;
}

const WORDING: Readonly<Record<CheckTypeId, CheckTypeWording>> = Object.freeze({
  gfci: {
    title: { en: 'GFCI protection', zh: 'GFCI 插座（漏电保护插座）' },
    pass: { en: 'GFCI protection devices were observed at the {areas}; operational testing was not performed', zh: '{areas}可见 GFCI 漏电保护装置；未做功能测试' },
    passTested: { en: 'GFCI protection at the {areas} responded properly when tested', zh: '{areas}的 GFCI 漏电保护测试时动作正常' },
    // A defect never claims a test (E2E walkthrough 2026-10-16): 缺陷 also covers "not a GFCI device" or a
    // missing receptacle, which no one tested; the finding it points to carries what was actually seen.
    defect: { en: 'a deficiency in GFCI protection was observed at the {areas}', zh: '{areas}的 GFCI 漏电保护存在缺陷' },
    unable: { en: 'GFCI protection at the {areas} could not be tested', zh: '{areas}的 GFCI 保护未能测试' }
  },
  // "and/or": a row records that alarms were there, not which kind — a bedroom row is a smoke alarm only.
  smokeCo: {
    title: { en: 'Smoke / carbon monoxide alarms', zh: '烟雾 / 一氧化碳报警器' },
    pass: { en: 'smoke and/or carbon monoxide alarms were observed at the {areas}; operational testing was not performed', zh: '{areas}的烟雾 / 一氧化碳报警器在位；未做功能测试' },
    passTested: { en: 'smoke and/or carbon monoxide alarms at the {areas} were observed and responded when tested', zh: '{areas}的烟雾 / 一氧化碳报警器在位，测试时有响应' },
    defect: { en: 'a smoke or carbon monoxide alarm deficiency was observed at the {areas}', zh: '{areas}的烟雾 / 一氧化碳报警器存在缺陷' },
    unable: { en: 'alarms at the {areas} could not be observed', zh: '{areas}的报警器未能观察' }
  },
  outlets: {
    title: { en: 'Receptacles and switches', zh: '插座与开关' },
    pass: { en: 'a representative number of receptacles and switches at the {areas} was observed; operational testing was not performed', zh: '{areas}的插座与开关已抽查观察；未做功能测试' },
    passTested: { en: 'a representative number of receptacles and switches at the {areas} was tested and functioned properly', zh: '{areas}的插座与开关抽查功能正常' },
    defect: { en: 'a deficiency was observed at a receptacle or switch at the {areas}', zh: '{areas}的插座或开关存在缺陷' },
    unable: { en: 'receptacles at the {areas} could not be tested', zh: '{areas}的插座未能测试' }
  },
  windowsDoors: {
    title: { en: 'Windows and doors', zh: '门窗' },
    pass: { en: 'a representative number of windows and doors at the {areas} were observed; no deficiency was noted', zh: '{areas}的门窗已抽查观察，未见缺陷' },
    passTested: { en: 'a representative number of windows and doors at the {areas} were operated and functioned properly', zh: '{areas}的门窗抽查开合正常' },
    defect: { en: 'a window or door deficiency was observed at the {areas}', zh: '{areas}的门窗存在缺陷' },
    unable: { en: 'windows or doors at the {areas} could not be operated', zh: '{areas}的门窗未能试开' }
  },
  stairsRails: {
    title: { en: 'Stairs, handrails and guardrails', zh: '楼梯、扶手与栏杆' },
    pass: { en: 'stairs, handrails and guardrails at the {areas} were inspected and no deficiencies were observed', zh: '{areas}的楼梯、扶手与栏杆已检查，未发现缺陷' },
    defect: { en: 'stairs, handrails or guardrails at the {areas} showed a deficiency', zh: '{areas}的楼梯、扶手或栏杆存在缺陷' },
    unable: { en: 'stairs at the {areas} could not be inspected', zh: '{areas}的楼梯未能检查' }
  },
  surfaces: {
    title: { en: 'Walls, ceilings and floors', zh: '墙壁、天花板、地板' },
    pass: { en: 'walls, ceilings and floors at the {areas} were inspected and no deficiencies were observed', zh: '{areas}的墙壁、天花板、地板已检查，未发现缺陷' },
    defect: { en: 'walls, ceilings or floors at the {areas} showed a deficiency', zh: '{areas}的墙壁、天花板、地板存在缺陷' },
    unable: { en: 'surfaces at the {areas} could not be inspected', zh: '{areas}的墙壁、天花板、地板未能检查' }
  },
  egress: {
    title: { en: 'Bedroom Window Operation — Supplemental Observation', zh: '卧室窗户开启情况（补充观察）' },
    pass: { en: 'a window at the {areas} was observed; it was not operated', zh: '{areas}的窗户已观察；未试开' },
    passTested: { en: 'a window at the {areas} was operated and opened', zh: '{areas}已试开一扇窗，能打开' },
    defect: { en: 'a window deficiency was observed at the {areas}; further evaluation is recommended', zh: '{areas}的窗户存在缺陷，建议进一步评估' },
    unable: { en: 'windows at the {areas} could not be operated', zh: '{areas}的窗户未能试开' }
  },
  tpr: {
    title: { en: 'Temperature and pressure relief valve', zh: '热水器泄压阀（T&P 阀）' },
    pass: { en: 'the temperature and pressure relief valve and its discharge line were present and appeared properly installed; the valve was not operated', zh: '热水器泄压阀与排放管在位、安装看起来正确；未操作该阀' },
    defect: { en: 'the temperature and pressure relief valve or its discharge line showed a deficiency', zh: '热水器泄压阀或其排放管存在缺陷' },
    unable: { en: 'the temperature and pressure relief valve could not be inspected', zh: '热水器泄压阀未能检查' }
  },
  garageDoor: {
    title: { en: 'Garage door safety reverse', zh: '车库门遇阻反弹与安全感应器' },
    pass: { en: 'the garage door at the {areas} was observed; the auto-reverse test was not performed', zh: '{areas}的车库门已观察；未做遇阻反弹测试' },
    passTested: { en: 'the garage door opener at the {areas} reversed when tested', zh: '{areas}的车库门开门机测试时遇阻反弹正常' },
    defect: { en: 'a deficiency was observed at the garage door or opener at the {areas}', zh: '{areas}的车库门或开门机存在缺陷' },
    unable: { en: 'the garage door opener at the {areas} could not be tested', zh: '{areas}的车库门开门机未能测试' }
  },
  wdi: {
    title: { en: 'Wood-destroying insects — supplemental observation', zh: '白蚁及蛀木害虫（WDI · 补充观察）' },
    pass: { en: 'no evidence of wood-destroying insects was observed at the {areas} at the time of inspection', zh: '{areas}检查时未见白蚁及蛀木害虫迹象' },
    defect: { en: 'evidence suggestive of wood-destroying insects was observed at the {areas}', zh: '{areas}见疑似白蚁及蛀木害虫迹象' },
    unable: { en: 'the {areas} was not inspected for wood-destroying insects', zh: '{areas}未检查白蚁及蛀木害虫迹象' }
  },
  bathExhaust: {
    title: { en: 'Bathroom ventilation', zh: '浴室排风' },
    pass: { en: 'bathroom ventilation at the {areas} was observed (exhaust fan or openable window); where the exhaust duct was visible, it terminated at the exterior', zh: '{areas}的浴室排风（排风扇或可开窗）已观察；排风管可见处通到室外' },
    passTested: { en: 'bathroom ventilation at the {areas} operated (exhaust fan or openable window); where the exhaust duct was visible, it terminated at the exterior', zh: '{areas}的浴室排风（排风扇或可开窗）运作正常；排风管可见处通到室外' },
    defect: { en: 'a bathroom ventilation deficiency was observed at the {areas}', zh: '{areas}的浴室排风存在缺陷' },
    unable: { en: 'bathroom ventilation at the {areas} could not be operated or observed', zh: '{areas}的浴室排风未能试运行或观察' }
  },
  // Kitchen exhaust DUCTING is mechanical ventilation (197-5.12(a)(4)); the range hood appliance is not
  // evaluated and a recirculating hood is a configuration, never a deficiency (ratification §6).
  kitchenVent: {
    title: { en: 'Kitchen exhaust ducting', zh: '厨房排风管道' },
    pass: { en: 'kitchen exhaust ducting at the {areas} was observed where visible; no deficiency was noted', zh: '{areas}的厨房排风管道可见处已观察，未发现缺陷' },
    defect: { en: 'a visible kitchen exhaust duct at the {areas} did not terminate at the exterior or was damaged', zh: '{areas}可见的厨房排风管未通到室外或有损坏' },
    unable: { en: 'kitchen exhaust ducting at the {areas} could not be observed', zh: '{areas}的厨房排风管道未能观察' }
  }
});

// 2026-10-28 (iPhone real-photo run 2026-10-26, #10 — the report contradicted itself):
//   - the 安全复核 / Safety Review area is a whole-house REVIEW of checks that live in their own areas, not a
//     place in the house: its name never fills {areas} ("observed at the Safety Review"). A review row is
//     located at the house, and only speaks for its check where no area row of that check was recorded —
//     a passed review row beside a water heater recorded as not present no longer says the TPR valve was
//     "present and appeared properly installed";
//   - a pass sentence that DENIES the test ("operational testing was not performed", "it was not operated")
//     is not printed where the report says otherwise elsewhere: the systems summary does not deny it for a
//     row whose note or own sentence says the device was tested / operated (that sentence may print), and a
//     bedroom-window row does not deny it where the room's windows-and-doors row records that its windows
//     were operated — those say what was observed, and claim neither;
//   - an AI-written sentence never claims a test or an operation that neither the test fact nor the
//     inspector's note records (storedSentenceFor).
export const REVIEW_AREA_KIND = 'safety';
const WHOLE_HOUSE: Bilingual = Object.freeze({ en: 'house', zh: '全屋' });
const REVIEWED: Readonly<Record<'one' | 'many', Bilingual>> = Object.freeze({
  one: { en: 'Reviewed; see the {areas} section.', zh: '已复核，详见{areas}一节。' },
  many: { en: 'Reviewed; see the {areas} sections.', zh: '已复核，详见{areas}各节。' }
});
const PASS_OBSERVED: Readonly<Partial<Record<CheckTypeId, Bilingual>>> = Object.freeze({
  gfci: { en: 'GFCI protection devices were observed at the {areas}', zh: '{areas}可见 GFCI 漏电保护装置' },
  smokeCo: { en: 'smoke and/or carbon monoxide alarms were observed at the {areas}', zh: '{areas}的烟雾 / 一氧化碳报警器在位' },
  outlets: { en: 'a representative number of receptacles and switches at the {areas} was observed', zh: '{areas}的插座与开关已抽查观察' },
  egress: { en: 'a window at the {areas} was observed', zh: '{areas}的窗户已观察' },
  garageDoor: { en: 'the garage door at the {areas} was observed', zh: '{areas}的车库门已观察' }
});

// The inspector's own words record a test / an operation (and do not negate it).
const TEST_WORDS_EN = /\b(test|tested|testing|operated|operating|operation|tripped|trips|responded|responds|opened|cycled|reset)\b/i;
const TEST_WORDS_ZH = /(测试|测了|试开|试运行|试过|操作|开合|跳闸|复位|运行正常|运作正常|能打开|可以打开|打得开|开启正常|有响应)/;

export function wordsRecordTest(text: string): boolean {
  return splitSentences(text).some((s) => (TEST_WORDS_EN.test(s) && !NEGATION_EN.test(s)) || (TEST_WORDS_ZH.test(s) && !NEGATION_ZH.test(s)));
}

// Whether the row's own record says, in words, that the device was tested / operated although the test
// fact is not set: its note, or a sentence the inspector wrote that still stands under the row's result.
function recordsTestInWords(item: Pick<V2ChecklistItem, 'status'> & { inspectorNote?: string; reportSentence?: V2ChecklistItem['reportSentence'] }): boolean {
  if (wordsRecordTest(item.inspectorNote ?? '')) return true;
  const own = item.reportSentence ?? null;
  return own !== null && own.source === 'inspector' && basisStatus(own.basis) === item.status && (claimsTest(own.en) || claimsTest(own.zh));
}

// The pass wording of an untested row: the observed-only sentence where denying the test would contradict
// the record, the library's own pass sentence otherwise.
function untestedPass(checkType: CheckTypeId, contradictsDenial: boolean): Bilingual {
  return contradictsDenial ? (PASS_OBSERVED[checkType] ?? WORDING[checkType].pass) : WORDING[checkType].pass;
}

// Printed under the WDI paragraph (ratification §7): an ordinary observation is never an NPMA-33.
const WDI_SCOPE_NOTE: Bilingual = Object.freeze({
  en: 'This is not a wood-destroying insect inspection; a separate inspection by a NYS DEC-certified applicator is available.',
  zh: '本报告不是白蚁检查；可另行委托纽约州 DEC 认证施药人员做独立检查。'
});

// The library sentences a row falls back to while no AI / inspector sentence stands (Final Owner
// Decision 2026-09-14 D-CA-2): a Defect row prints what the inspector noted — the primary fact
// source — and the referral to the professional the rules library names for the row. Nothing is
// invented: with no note the sentence only states that a deficiency was observed.
const GENERIC: Readonly<Record<'pass' | 'defect' | 'defectNote' | 'referral', Bilingual>> = Object.freeze({
  pass: { en: 'Inspected; no deficiencies were observed at the time of inspection.', zh: '已检查，检查时未发现缺陷。' },
  defect: { en: 'A deficiency was observed at the time of inspection.', zh: '检查时发现缺陷。' },
  defectNote: { en: 'A deficiency was observed at the time of inspection: {note}', zh: '检查时发现缺陷：{note}' },
  referral: { en: 'Evaluation and repair by a {trade} is recommended.', zh: '建议由{trade}评估并修复。' }
});

function pick(locale: ReportOutputLanguage, text: Bilingual): string {
  return locale === 'zh-CN' ? text.zh : text.en;
}

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => values[key] ?? '');
}

function capitalize(sentence: string): string {
  return sentence.length > 0 ? sentence[0].toUpperCase() + sentence.slice(1) : sentence;
}

export function joinNames(locale: ReportOutputLanguage, names: readonly string[]): string {
  const list = [...new Set(names.filter((n) => n.trim().length > 0))];
  if (locale === 'zh-CN') return list.join('、');
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

function period(locale: ReportOutputLanguage): string {
  return locale === 'zh-CN' ? '。' : '.';
}

export function checkTypeTitle(locale: ReportOutputLanguage, checkType: CheckTypeId): string {
  return pick(locale, WORDING[checkType].title);
}

// Whether the check's pass sentence depends on the recorded test / operation fact — the item sheet
// offers that fact on exactly these rows, so every tested sentence can be reached by recording it.
export function checkTypeRecordsTest(checkType: CheckTypeId): boolean {
  return WORDING[checkType].passTested !== undefined;
}

function endsWithPunctuation(text: string): boolean {
  return /[.!?。！？]["'”』）)]*$/.test(text);
}

// The professional the report names for a Defect on the row, as a report-language label, or null.
export interface RowWordingOptions {
  tradeLabel?: string | null;
  // 2026-10-28 (iPhone run 10-26 #10: the half bath's two defects printed only "A deficiency was observed"):
  // what the AI concluded on a Defect row, already in the report language (src/report/defectFacts.ts) — the
  // fact a Defect row prints when the inspector wrote no note. The inspector's note always comes first.
  defectFacts?: readonly string[];
  // A Safety Review row whose check was recorded in these areas (report-language names): it points there.
  reviewOf?: readonly string[];
  // A bedroom-window row in a room whose windows-and-doors row records that the windows were operated.
  siblingOperated?: boolean;
}

// The referral a Defect sentence ends with — also the recommendation an other finding drafted from an AI
// observation starts with, so the sentence and the trade setting name the same professional.
export function referralSentence(locale: ReportOutputLanguage, tradeLabel: string): string {
  return fill(pick(locale, GENERIC.referral), { trade: tradeLabel });
}

// The LIBRARY sentence for ONE observed row (checkedOk / issueFound) — what the report prints until
// the AI or the inspector writes the row's own sentence (rowSentenceFor). '' for every other result —
// an unable / not-present row is worded by its own block, a not-recorded row says nothing.
//   checkedOk   the check type's uniform sentence, or the generic "inspected, no deficiency"
//   issueFound  the deficiency with the inspector's note as the fact (CA-14: the note is the primary
//               source), then the referral to the row's professional (§15.2 / §15.6)
export function rowReportSentence(locale: ReportOutputLanguage, item: Pick<V2ChecklistItem, 'templateId' | 'status'> & { operationalTestPerformed?: boolean; inspectorNote?: string }, areaName: string, options: RowWordingOptions = {}): string {
  const checkType = checkTypeOfTemplateRow(item.templateId);
  if (item.status === 'checkedOk') {
    if (checkType) {
      const reviewOf = [...new Set((options.reviewOf ?? []).filter((n) => n.trim().length > 0))];
      if (reviewOf.length > 0) return fill(pick(locale, REVIEWED[reviewOf.length === 1 ? 'one' : 'many']), { areas: joinNames(locale, reviewOf) });
      const w = WORDING[checkType];
      const tested = w.passTested !== undefined && item.operationalTestPerformed === true;
      // (the note itself does not print on a passed row, so the row's own sentence keeps the library's
      // denial; the systems summary, which also speaks for a standing AI / inspector sentence, does not)
      const passWording = tested && w.passTested ? w.passTested : untestedPass(checkType, checkType === 'egress' && options.siblingOperated === true);
      return capitalize(fill(pick(locale, passWording), { areas: areaName })) + period(locale);
    }
    return pick(locale, GENERIC.pass);
  }
  if (item.status === 'issueFound') {
    // the inspector's note is the primary fact (CA-14); without one, what the AI concluded on the row
    const note = (item.inspectorNote ?? '').trim();
    const aiFacts = [...new Set((options.defectFacts ?? []).map((f) => f.trim()).filter((f) => f.length > 0))].join(locale === 'zh-CN' ? '' : ' ');
    const stated = note.length > 0 ? note : aiFacts;
    const fact = stated.length > 0 ? fill(pick(locale, GENERIC.defectNote), { note: stated }) + (endsWithPunctuation(stated) ? '' : period(locale)) : pick(locale, GENERIC.defect);
    const trade = (options.tradeLabel ?? '').trim();
    const referral = trade.length > 0 ? fill(pick(locale, GENERIC.referral), { trade }) : '';
    return referral.length > 0 ? `${fact}${locale === 'zh-CN' ? '' : ' '}${referral}` : fact;
  }
  return '';
}

export interface SystemSummary {
  checkType: CheckTypeId;
  title: string;
  sentence: string;
  // How the sentence was built — the item sheet shows the parts it is made of.
  clear: string[];
  deficient: string[];
  notTested: string[];
}

// The same check across the house, one paragraph per check type, in catalogue order. A type appears
// once at least one of its rows has a recorded result (checkedOk / issueFound / a true Unable).
export function systemSummaries(locale: ReportOutputLanguage, areas: readonly V2Area[], items: readonly V2ChecklistItem[]): SystemSummary[] {
  const enabled = areas.filter((a) => a.enabled);
  const areaById = new Map(enabled.map((a) => [a.id, a] as const));
  const operatedRooms = roomsWithOperatedWindows(items);
  const house = pick(locale, WHOLE_HOUSE);
  const out: SystemSummary[] = [];
  for (const checkType of CHECK_TYPE_ORDER) {
    const w = WORDING[checkType];
    const clear: string[] = [];
    const clearTested: string[] = [];
    // observed only — where denying the test would contradict the row's own record (2026-10-28)
    const clearObserved: string[] = [];
    const deficient: string[] = [];
    const notTested: string[] = [];
    const reviewRows: V2ChecklistItem[] = [];
    let areaRowRecorded = false;
    for (const item of items) {
      if (checkTypeOfTemplateRow(item.templateId) !== checkType) continue;
      const area = areaById.get(item.areaId);
      if (!area) continue;
      if (area.kind === REVIEW_AREA_KIND) {
        reviewRows.push(item);
        continue;
      }
      if (item.status !== 'notChecked') areaRowRecorded = true;
      const name = areaDisplayName(locale, area);
      if (item.status === 'checkedOk') {
        if (w.passTested && item.operationalTestPerformed === true) clearTested.push(name);
        else if (PASS_OBSERVED[checkType] && (recordsTestInWords(item) || (checkType === 'egress' && operatedRooms.has(item.areaId)))) clearObserved.push(name);
        else clear.push(name);
      } else if (item.status === 'issueFound') deficient.push(name);
      else if (item.status === 'unableToInspect' && item.reasonCode !== 'notInstalled') notTested.push(name);
    }
    // 2026-10-28: the whole-house review rows. A deficiency there is always stated (at the house, unless an
    // area row already states one); a clear or untested review speaks only where no area row was recorded.
    for (const item of reviewRows) {
      if (item.status === 'issueFound') {
        if (deficient.length === 0 || deficient.every((n) => n === house)) deficient.push(house);
      } else if (!areaRowRecorded) {
        if (item.status === 'checkedOk') (w.passTested && item.operationalTestPerformed === true ? clearTested : clear).push(house);
        else if (item.status === 'unableToInspect' && item.reasonCode !== 'notInstalled') notTested.push(house);
      }
    }
    if (clear.length + clearTested.length + clearObserved.length + deficient.length + notTested.length === 0) continue;
    const parts: string[] = [];
    if (deficient.length > 0) {
      const seeFindings = locale === 'zh-CN' ? '（见问题记录）' : ' (see the findings)';
      parts.push(capitalize(fill(pick(locale, w.defect), { areas: joinNames(locale, deficient) })) + seeFindings + period(locale));
    }
    if (clearTested.length > 0 && w.passTested) parts.push(capitalize(fill(pick(locale, w.passTested), { areas: joinNames(locale, clearTested) })) + period(locale));
    if (clear.length > 0) parts.push(capitalize(fill(pick(locale, w.pass), { areas: joinNames(locale, clear) })) + period(locale));
    const observedWording = PASS_OBSERVED[checkType];
    if (clearObserved.length > 0 && observedWording) parts.push(capitalize(fill(pick(locale, observedWording), { areas: joinNames(locale, clearObserved) })) + period(locale));
    if (notTested.length > 0) {
      const seeLimitations = locale === 'zh-CN' ? '（见检查限制）' : ' (see the limitations)';
      parts.push(capitalize(fill(pick(locale, w.unable), { areas: joinNames(locale, notTested) })) + seeLimitations + period(locale));
    }
    if (checkType === 'wdi') parts.push(pick(locale, WDI_SCOPE_NOTE));
    out.push({ checkType, title: pick(locale, w.title), sentence: parts.join(locale === 'zh-CN' ? '' : ' '), clear: [...new Set([...clearTested, ...clear, ...clearObserved])], deficient: [...new Set(deficient)], notTested: [...new Set(notTested)] });
  }
  return out;
}

// The areas whose windows-and-doors row records that the windows were operated (2026-10-28).
function roomsWithOperatedWindows(items: readonly V2ChecklistItem[]): Set<string> {
  return new Set(items.filter((i) => checkTypeOfTemplateRow(i.templateId) === 'windowsDoors' && i.status === 'checkedOk' && i.operationalTestPerformed === true).map((i) => i.areaId));
}

// Where a row's library sentence says it is, and what it may claim (2026-10-28): a Safety Review row is at the
// house and, once its check was recorded in an area, points to that area's section; a bedroom-window row
// does not deny an operation the room's windows-and-doors row records. The report and the item sheet both
// word a row through this, so what the sheet previews is what prints.
export interface RowContext {
  areaName: string;
  options: Pick<RowWordingOptions, 'reviewOf' | 'siblingOperated'>;
}

export function rowContextFor(
  locale: ReportOutputLanguage,
  area: V2Area,
  item: Pick<V2ChecklistItem, 'templateId'>,
  areas: readonly V2Area[],
  items: readonly V2ChecklistItem[]
): RowContext {
  const checkType = checkTypeOfTemplateRow(item.templateId);
  if (area.kind === REVIEW_AREA_KIND) {
    if (!checkType) return { areaName: pick(locale, WHOLE_HOUSE), options: {} };
    const ordered = areas.filter((a) => a.enabled && a.kind !== REVIEW_AREA_KIND && a.inspectionId === area.inspectionId).sort((a, b) => a.sortOrder - b.sortOrder);
    const recorded = ordered.filter((a) => items.some((i) => i.areaId === a.id && i.status !== 'notChecked' && checkTypeOfTemplateRow(i.templateId) === checkType));
    return { areaName: pick(locale, WHOLE_HOUSE), options: recorded.length > 0 ? { reviewOf: recorded.map((a) => areaDisplayName(locale, a)) } : {} };
  }
  if (checkType === 'egress' && roomsWithOperatedWindows(items.filter((i) => i.areaId === area.id)).size > 0) return { areaName: areaDisplayName(locale, area), options: { siblingOperated: true } };
  return { areaName: areaDisplayName(locale, area), options: {} };
}

// The facts a stored sentence was written from, fingerprinted (CA-14 order): the inspector's result
// and note, the recorded test fact, the photo notes, the AI observations of the row and the advanced
// review that stood when the sentence was written. A change in any of them makes an AI-written
// sentence stale (D-CA-9).
export interface WordingFacts {
  photoNotes: readonly string[];
  aiObservations: readonly string[];
  advancedReview?: string | null;
  structuralConcern?: boolean;
}

export function wordingBasisOf(item: Pick<V2ChecklistItem, 'status' | 'inspectorNote'> & { operationalTestPerformed?: boolean; structuralConcern?: boolean }, facts: WordingFacts): string {
  const parts = [
    item.status,
    item.operationalTestPerformed === true ? 'tested' : 'untested',
    (facts.structuralConcern ?? item.structuralConcern) === true ? 'structural' : 'plain',
    item.inspectorNote.trim(),
    facts.photoNotes.map((n) => n.trim()).filter((n) => n.length > 0).join('|'),
    facts.aiObservations.map((n) => n.trim()).filter((n) => n.length > 0).join('|'),
    (facts.advancedReview ?? '').trim()
  ].join('');
  let hash = 5381;
  for (let i = 0; i < parts.length; i += 1) hash = ((hash << 5) + hash + parts.charCodeAt(i)) | 0;
  return `${item.status}:${(hash >>> 0).toString(16)}`;
}

// The result a basis was written under (the part before the colon).
export function basisStatus(basis: string): string {
  return basis.split(':')[0] ?? '';
}

// Whether the sentence stored on the row still stands (D-CA-9):
//   ai         only while every fact it was written from is unchanged;
//   inspector  the inspector's own words stand while the RESULT is unchanged — a note or a photo note
//              may move without erasing what the inspector chose to say; a result change (Defect →
//              Pass …) does erase it, so a defect sentence never survives on a non-defect row (§16.3).
export function storedSentenceCurrent(item: Pick<V2ChecklistItem, 'reportSentence' | 'status'>, basis: string): boolean {
  const stored = item.reportSentence;
  if (!stored) return false;
  if (stored.source === 'inspector') return basisStatus(stored.basis) === item.status;
  return stored.basis === basis;
}

// The sentence stored on the row when it still stands, in the report language.
export function storedSentenceFor(
  locale: ReportOutputLanguage,
  item: Pick<V2ChecklistItem, 'reportSentence' | 'status'> & { templateId?: string | null; operationalTestPerformed?: boolean; inspectorNote?: string },
  basis: string
): string | null {
  const stored = item.reportSentence;
  if (!stored || !storedSentenceCurrent(item, basis)) return null;
  let text = (locale === 'zh-CN' ? stored.zh : stored.en).trim();
  // 2026-10-28 (iPhone run 10-26 #10: a bedroom window "not operated" in the summary, "operation checked" under
  // the row): on a passed row of a check whose wording depends on the recorded test, an AI sentence never
  // claims an operation or a test that neither the test fact nor the inspector's note records — that sentence
  // goes, the rest stays (the wording guard's rule). The systems summary says "not tested" for that row.
  const checkType = item.templateId === undefined ? null : checkTypeOfTemplateRow(item.templateId);
  if (stored.source === 'ai' && item.status === 'checkedOk' && checkType !== null && checkTypeRecordsTest(checkType) && item.operationalTestPerformed !== true && !wordsRecordTest(item.inspectorNote ?? '')) text = withoutUnrecordedTestClaims(text);
  return text.length > 0 ? text : null;
}

const TEST_CLAIM_EN: readonly RegExp[] = Object.freeze([
  /\b(operated|tested|responded|functioned|functioning|opened and closed)\b/i,
  /\boperat(?:es|ing)\s+(?:normally|properly|as intended|correctly)\b/i,
  /\boperation\b[^.;]{0,24}\b(checked|confirmed|verified|tested|normal|satisfactory)\b/i
]);
const NEGATION_EN = /\b(not|never|unable|cannot|without)\b|n['’]t\b/i;
const TEST_CLAIM_ZH = /(测试时|测试正常|已测试|试开|试运行|开合正常|运作正常|运行正常|动作正常|功能正常|操作正常|已操作|有响应|能打开|可正常开启)/;
const NEGATION_ZH = /(未|没有|没|无法|不能)/;

function sentenceClaimsTest(sentence: string): boolean {
  const claimsEn = TEST_CLAIM_EN.some((p) => p.test(sentence)) && !NEGATION_EN.test(sentence);
  const claimsZh = TEST_CLAIM_ZH.test(sentence) && !NEGATION_ZH.test(sentence);
  return claimsEn || claimsZh;
}

export function claimsTest(text: string): boolean {
  return splitSentences(text).some(sentenceClaimsTest);
}

export function withoutUnrecordedTestClaims(text: string): string {
  const kept = splitSentences(text).filter((sentence) => !sentenceClaimsTest(sentence));
  // a clause after a removed one may start lower-case, a kept clause may end on its semicolon
  const joined = kept.join('').trim().replace(/[;；]$/, (mark) => (mark === '；' ? '。' : '.'));
  return capitalize(joined);
}

// Owner device test 2026-09-21: the inspector may write the sentence in Chinese. Which language of a
// standing inspector-authored sentence is NOT the inspector's last word — the side that still holds
// what stood before, until the AI renders the inspector's words again or the inspector edits it:
//   'en'  the inspector wrote Chinese last → the ENGLISH the client reads is out of sync (the closeout
//         blocks on it: a delivered report must say what the inspector last wrote);
//   'zh'  the inspector wrote English last → the Chinese reference is out of sync (shown, never blocks);
//   null  nothing is out of sync (library sentence, AI sentence, or no standing sentence).
export function unsyncedWordingLanguage(item: Pick<V2ChecklistItem, 'reportSentence' | 'status'>, basis: string): 'en' | 'zh' | null {
  const stored = item.reportSentence;
  if (!stored || stored.source !== 'inspector' || !storedSentenceCurrent(item, basis)) return null;
  return stored.edited === 'zh' ? 'en' : 'zh';
}

// What the report prints for an observed row: the sentence written for it while it stands, otherwise
// the library sentence. The item sheet previews through this same function.
export function rowSentenceFor(
  locale: ReportOutputLanguage,
  item: Pick<V2ChecklistItem, 'templateId' | 'status' | 'inspectorNote' | 'reportSentence'> & { operationalTestPerformed?: boolean; structuralConcern?: boolean },
  areaName: string,
  facts: WordingFacts,
  options: RowWordingOptions = {}
): string {
  if (item.status !== 'checkedOk' && item.status !== 'issueFound') return '';
  return storedSentenceFor(locale, item, wordingBasisOf(item, facts)) ?? rowReportSentence(locale, item, areaName, options);
}

export function systemSummaryFor(locale: ReportOutputLanguage, areas: readonly V2Area[], items: readonly V2ChecklistItem[], templateId: string | null): SystemSummary | null {
  const checkType = checkTypeOfTemplateRow(templateId);
  if (!checkType) return null;
  return systemSummaries(locale, areas, items).find((s) => s.checkType === checkType) ?? null;
}
